import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileCheck, Download, CheckCircle, Mail } from 'lucide-react';
import { markResourceDownloaded } from '../lib/leadMagnetSuppression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResourceDetailPage() {
  const { slug } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchResource = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/resources/${slug}`);
        setResource(response.data);
      } catch (error) {
        console.error('Error fetching resource:', error);
        setError('Resource not found');
      } finally {
        setLoading(false);
      }
    };
    fetchResource();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      toast.error('First name, last name, and email are required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/resources/download`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        resource_id: resource.resource_id,
        resource_name: resource.title,
      });
      setSubmitted(true);
      markResourceDownloaded(resource.resource_id, formData.email);
      toast.success('Success! Your download is ready.');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (error || !resource) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-[#0A0F1C] mb-4">Resource Not Found</h1>
          <Link to="/resources" className="text-[#1D4ED8] hover:underline">
            ← Back to Resources
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="section-padding bg-white" data-testid="resource-detail-hero">
        <div className="container-custom">
          <Link 
            to="/resources" 
            className="inline-flex items-center gap-2 text-[#475569] hover:text-[#1D4ED8] mb-8"
            data-testid="back-to-resources"
          >
            <ArrowLeft size={18} />
            Back to Resources
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#FFF7ED] rounded-lg flex items-center justify-center">
                  <FileCheck size={32} className="text-[#F97316]" />
                </div>
                <span className="badge-orange capitalize">{resource.resource_type}</span>
              </div>
              
              <h1 className="heading-1 text-3xl sm:text-4xl lg:text-5xl text-[#0A0F1C] mb-6" data-testid="resource-title">
                {resource.title}
              </h1>
              
              <p className="body-text text-[#475569] text-lg mb-8" data-testid="resource-description">
                {resource.description}
              </p>
              
              {/* What's Inside */}
              <div className="mb-8">
                <h3 className="font-bold text-[#0A0F1C] mb-4">What You'll Get:</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#475569]">Step-by-step actionable checklist</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#475569]">Based on real-world business experience</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#475569]">Printable PDF format</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[#1D4ED8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#475569]">Instant download access</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right - Download Form */}
            <div className="lg:sticky lg:top-24">
              <div className="bg-[#FAFAFA] p-8 border border-[#E2E8F0]">
                {!submitted ? (
                  <>
                    <h3 className="heading-3 text-xl text-[#0A0F1C] mb-2">Get Your Free Download</h3>
                    <p className="text-sm text-[#475569] mb-6">
                      Enter your details below to receive instant access to this resource.
                    </p>
                    
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="resource-download-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-[#0A0F1C] mb-2">
                            First Name <span className="text-[#EF4444]">*</span>
                          </label>
                          <Input
                            type="text"
                            placeholder="First name"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                            className="form-input"
                            data-testid="resource-form-first-name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#0A0F1C] mb-2">
                            Last Name <span className="text-[#EF4444]">*</span>
                          </label>
                          <Input
                            type="text"
                            placeholder="Last name"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                            className="form-input"
                            data-testid="resource-form-last-name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A0F1C] mb-2">
                          Email Address <span className="text-[#EF4444]">*</span>
                        </label>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="form-input"
                          data-testid="resource-form-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A0F1C] mb-2">
                          Phone Number <span className="text-[#94A3B8] text-xs font-normal">(optional)</span>
                        </label>
                        <Input
                          type="tel"
                          placeholder="XXX-XXX-XXXX"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="form-input"
                          data-testid="resource-form-phone"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={submitting}
                        className="btn-primary-orange w-full"
                        data-testid="resource-form-submit"
                      >
                        {submitting ? 'Processing...' : 'Get Free Access'}
                      </Button>
                      <p className="text-xs text-[#94A3B8] text-center">
                        We respect your privacy. Unsubscribe at any time.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center" data-testid="resource-download-success">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="heading-3 text-xl text-[#0A0F1C] mb-2">Success!</h3>
                    <p className="text-[#475569] mb-6">
                      Your download is ready. Click the button below to access your resource.
                    </p>
                    <a 
                      href={resource.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary-orange w-full inline-flex items-center justify-center gap-2"
                      data-testid="resource-download-link"
                    >
                      <Download size={18} />
                      Download Now
                    </a>
                    <p className="text-xs text-[#94A3B8] mt-4">
                      <Mail size={14} className="inline mr-1" />
                      A copy has also been sent to your email.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related CTA */}
      <section className="section-padding bg-[#0F172A]" data-testid="resource-related-cta">
        <div className="container-custom text-center">
          <h2 className="heading-2 text-3xl text-white mb-4">
            Ready for More Than a Checklist?
          </h2>
          <p className="text-[#94A3B8] text-lg mb-8 max-w-2xl mx-auto">
            Get personalized guidance and hands-on support to implement these strategies in your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/schedule" 
              className="btn-primary-orange inline-flex items-center justify-center gap-2"
              data-testid="resource-cta-book"
            >
              FREE 20-Minute Business Jumpstart Session
            </Link>
            <Link 
              to="/services" 
              className="btn-outline border-white text-white hover:bg-white/10 inline-flex items-center justify-center"
              data-testid="resource-cta-services"
            >
              View Services
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
